import { HTTP_URL } from "../config/backend";
import { useState, useEffect, useCallback, useMemo } from "react";

const API = HTTP_URL;

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("ru", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" }); }
  catch { return s?.slice(0,16) || "—"; }
}
function fmtDur(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec/60), s = sec%60;
  return m > 0 ? `${m}м ${s}с` : `${s}с`;
}

const C = {
  bg:"#080810", surface:"#0f0f1a", surface2:"#161625",
  border:"#ffffff12", border2:"#ffffff20", text:"#e8e8f0", muted:"#6060a0",
  pink:"#ff3cac", cyan:"#00f5ff", yellow:"#ffe600",
  green:"#3dffa0", purple:"#9b5de5", red:"#ff3355", orange:"#ff8c00",
};

function Badge({ text, color="muted" }) {
  const map = { green:["#3dffa0","rgba(61,255,160,.12)","rgba(61,255,160,.3)"], red:["#ff3355","rgba(255,51,85,.12)","rgba(255,51,85,.3)"], yellow:["#ffe600","rgba(255,230,0,.12)","rgba(255,230,0,.3)"], purple:["#9b5de5","rgba(155,93,229,.12)","rgba(155,93,229,.3)"], cyan:["#00f5ff","rgba(0,245,255,.12)","rgba(0,245,255,.3)"], muted:["#6060a0","rgba(255,255,255,.06)","#ffffff12"] };
  const [tc,bg,bc] = map[color]||map.muted;
  return <span style={{display:"inline-block",padding:"2px 8px",borderRadius:20,fontSize:11,fontWeight:700,background:bg,color:tc,border:`1px solid ${bc}`,fontFamily:"'Syne',sans-serif",whiteSpace:"nowrap"}}>{text}</span>;
}

function Btn({ children, onClick, color="muted", sm, disabled }) {
  const map = { pink:["#ff3cac","rgba(255,60,172,.12)","rgba(255,60,172,.3)"], green:["#3dffa0","rgba(61,255,160,.1)","rgba(61,255,160,.3)"], red:["#ff3355","rgba(255,51,85,.1)","rgba(255,51,85,.3)"], yellow:["#ffe600","rgba(255,230,0,.1)","rgba(255,230,0,.3)"], cyan:["#00f5ff","rgba(0,245,255,.1)","rgba(0,245,255,.3)"], muted:["#6060a0","rgba(255,255,255,.06)","#ffffff18"] };
  const [tc,bg,bc] = map[color]||map.muted;
  return <button onClick={onClick} disabled={disabled} style={{padding:sm?"4px 10px":"7px 14px",borderRadius:7,fontFamily:"'Syne',sans-serif",fontSize:sm?11:12,fontWeight:700,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.45:1,background:bg,color:tc,border:`1px solid ${bc}`,whiteSpace:"nowrap",transition:"opacity .15s"}}>{children}</button>;
}

function Input({ value, onChange, placeholder, type="text", style={} }) {
  return <input value={value} onChange={onChange} type={type} placeholder={placeholder} style={{background:C.surface2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 12px",color:C.text,fontFamily:"'Syne',sans-serif",fontSize:13,outline:"none",...style}} />;
}

function Select({ value, onChange, options, style={} }) {
  return <select value={value} onChange={onChange} style={{background:C.surface2,border:`1px solid ${C.border2}`,borderRadius:8,padding:"7px 10px",color:C.text,fontFamily:"'Syne',sans-serif",fontSize:12,outline:"none",cursor:"pointer",...style}}>{options.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select>;
}

function Pagination({ total, page, perPage, onPage, onPerPage }) {
  const pages = Math.ceil(total/perPage)||1;
  if (total<=perPage&&pages<=1) return null;
  const from=(page-1)*perPage+1, to=Math.min(page*perPage,total);
  const nums = [];
  for(let i=Math.max(1,page-2);i<=Math.min(pages,page+2);i++) nums.push(i);
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderTop:`1px solid ${C.border}`,flexWrap:"wrap",gap:10}}>
      <div style={{fontSize:12,color:C.muted,fontFamily:"monospace"}}>{from}–{to} из {total}</div>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <Select value={perPage} onChange={e=>onPerPage(Number(e.target.value))} options={[["10","10/стр"],["25","25/стр"],["50","50/стр"],["100","100/стр"]]} style={{padding:"4px 8px",fontSize:11}} />
        <Btn onClick={()=>onPage(1)} disabled={page===1} sm>«</Btn>
        <Btn onClick={()=>onPage(page-1)} disabled={page===1} sm>‹</Btn>
        {nums.map(p=><Btn key={p} onClick={()=>onPage(p)} color={p===page?"pink":"muted"} sm>{p}</Btn>)}
        <Btn onClick={()=>onPage(page+1)} disabled={page>=pages} sm>›</Btn>
        <Btn onClick={()=>onPage(pages)} disabled={page>=pages} sm>»</Btn>
      </div>
    </div>
  );
}

function SortTH({ label, field, sort, onSort }) {
  const active = sort?.field===field;
  return <th onClick={()=>field&&onSort(field)} style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:active?C.pink:C.muted,textTransform:"uppercase",letterSpacing:1,borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap",cursor:field?"pointer":"default",userSelect:"none"}}>{label}{active?(sort.dir==="asc"?" ↑":" ↓"):""}</th>;
}

function Card({ children, title, actions, count }) {
  return (
    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",marginBottom:20}}>
      {(title||actions)&&<div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}><div style={{display:"flex",alignItems:"center",gap:10}}>{title&&<span style={{fontWeight:700,fontSize:14}}>{title}</span>}{count!=null&&<span style={{fontSize:11,color:C.muted,fontFamily:"monospace"}}>{count} записей</span>}</div>{actions&&<div style={{display:"flex",gap:8}}>{actions}</div>}</div>}
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, color=C.pink }) {
  return (
    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",bottom:0,left:0,right:0,height:2,background:color}} />
      <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,fontWeight:600,marginBottom:8}}>{label}</div>
      <div style={{fontSize:30,fontWeight:800,letterSpacing:-1,color}}>{value}</div>
      {sub&&<div style={{fontSize:12,color:C.muted,marginTop:4}}>{sub}</div>}
    </div>
  );
}

function BarRow({ label, value, max, color=C.pink }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
      <span style={{fontSize:12,color:C.muted,width:150,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"monospace"}}>{label}</span>
      <div style={{flex:1,height:6,background:C.surface2,borderRadius:3,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.round(value/max*100)}%`,background:color,borderRadius:3,transition:"width .5s"}} /></div>
      <span style={{fontSize:12,color:C.muted,width:36,textAlign:"right",fontFamily:"monospace"}}>{value}</span>
    </div>
  );
}

function Toast({ msg, type, onHide }) {
  useEffect(()=>{if(!msg)return;const t=setTimeout(onHide,3000);return()=>clearTimeout(t);},[msg]);
  if(!msg)return null;
  return <div style={{position:"fixed",bottom:24,right:24,zIndex:300,background:C.surface2,border:`1px solid ${type==="ok"?"rgba(61,255,160,.4)":"rgba(255,51,85,.4)"}`,borderRadius:10,padding:"12px 18px",fontSize:13,color:C.text,boxShadow:"0 4px 20px rgba(0,0,0,.5)"}}>{type==="ok"?"✅ ":"❌ "}{msg}</div>;
}

function Modal({ open, onClose, title, children }) {
  if(!open)return null;
  return <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.75)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{background:C.surface,border:`1px solid ${C.border2}`,borderRadius:16,padding:28,width:400,maxWidth:"90vw",position:"relative"}}><button onClick={onClose} style={{position:"absolute",top:16,right:16,background:"none",border:"none",color:C.muted,fontSize:18,cursor:"pointer"}}>×</button><div style={{fontSize:16,fontWeight:700,marginBottom:18}}>{title}</div>{children}</div></div>;
}

function PageHeader({ title, sub, actions }) {
  return <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}><div><div style={{fontSize:26,fontWeight:800,letterSpacing:-1}}>{title}</div>{sub&&<div style={{fontSize:12,color:C.muted,fontFamily:"monospace",marginTop:4}}>{sub}</div>}</div>{actions&&<div style={{display:"flex",gap:10,flexWrap:"wrap"}}>{actions}</div>}</div>;
}

function Login({ onLogin }) {
  const [code,setCode]=useState(""), [err,setErr]=useState(""), [loading,setLoading]=useState(false);
  async function submit() {
    if(!code.trim())return; setLoading(true); setErr("");
    try { const r=await fetch(`${API}/api/promo/list`,{headers:{"x-owner-code":code}}); if(r.status===401){setErr("Неверный код");setLoading(false);return;} onLogin(code); } catch{setErr("Ошибка соединения");}
    setLoading(false);
  }
  return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",backgroundImage:"linear-gradient(rgba(255,60,172,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,60,172,.03) 1px,transparent 1px)",backgroundSize:"40px 40px"}}>
      <div style={{width:360,background:C.surface,border:`1px solid ${C.border2}`,borderRadius:20,padding:"40px 36px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:"linear-gradient(90deg,#ff3cac,#00f5ff,#ffe600)"}} />
        <div style={{fontSize:22,fontWeight:800,color:C.pink,marginBottom:4,fontFamily:"'Syne',sans-serif"}}>Neon<span style={{color:C.cyan}}>Party</span></div>
        <div style={{fontSize:12,color:C.muted,marginBottom:28,fontFamily:"monospace"}}>// admin panel</div>
        <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:8,fontWeight:600}}>Owner Code</div>
        <input type="password" value={code} onChange={e=>setCode(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="••••••••" style={{width:"100%",background:C.bg,border:`1px solid ${C.border2}`,borderRadius:10,padding:"12px 16px",color:C.text,fontFamily:"monospace",fontSize:14,outline:"none",letterSpacing:2,marginBottom:16}} />
        <button onClick={submit} disabled={loading} style={{width:"100%",background:C.pink,border:"none",borderRadius:10,padding:13,color:"#fff",fontFamily:"'Syne',sans-serif",fontSize:15,fontWeight:700,cursor:loading?"not-allowed":"pointer",opacity:loading?.6:1}}>{loading?"Проверка...":"Войти →"}</button>
        {err&&<div style={{color:C.red,fontSize:13,marginTop:10,textAlign:"center"}}>{err}</div>}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [ownerCode,setOwnerCode]=useState(()=>sessionStorage.getItem("admin_code")||"");
  const [tab,setTab]=useState("dashboard");
  const [toast,setToast]=useState({msg:"",type:"ok"});
  const showToast=(msg,type="ok")=>setToast({msg,type});
  const apiFetch=useCallback(async(path,opts={})=>{const r=await fetch(`${API}${path}`,{...opts,headers:{"x-owner-code":ownerCode,"Content-Type":"application/json",...(opts.headers||{})}});if(!r.ok)throw new Error(`HTTP ${r.status}`);return r.json();},[ownerCode]);
  if(!ownerCode)return <Login onLogin={c=>{sessionStorage.setItem("admin_code",c);setOwnerCode(c);}} />;
  const TABS=[{id:"dashboard",label:"Дашборд"},{id:"promo",label:"Промокоды"},{id:"orders",label:"Заказы"},{id:"sessions",label:"Сессии"},{id:"bugs",label:"Баг-репорты"},{id:"analytics",label:"Аналитика"},{id:"settings",label:"Настройки"},{id:"news",label:"Новости"},{id:"banner",label:"🎁 Акции"}];
  return (
    <div style={{minHeight:"100vh",background:C.bg,color:C.text,fontFamily:"'Syne',sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <div style={{position:"fixed",left:0,top:0,bottom:0,width:220,background:C.surface,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",zIndex:10}}>
        <div style={{padding:"20px",borderBottom:`1px solid ${C.border}`}}><div style={{fontSize:16,fontWeight:800,color:C.pink}}>Neon<span style={{color:C.cyan}}>Party</span></div><div style={{fontSize:10,color:C.muted,fontFamily:"monospace",marginTop:2}}>admin panel</div></div>
        <nav style={{flex:1,padding:"14px 10px",display:"flex",flexDirection:"column",gap:3}}>
          {TABS.map(t=><div key={t.id} onClick={()=>setTab(t.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,color:tab===t.id?C.text:C.muted,background:tab===t.id?C.surface2:"transparent",border:`1px solid ${tab===t.id?C.border2:"transparent"}`,transition:"all .15s"}}><div style={{width:6,height:6,borderRadius:"50%",flexShrink:0,background:tab===t.id?C.pink:C.border,boxShadow:tab===t.id?`0 0 8px ${C.pink}`:"none"}} />{t.label}</div>)}
        </nav>
        <div style={{padding:"14px 20px",borderTop:`1px solid ${C.border}`}}><button onClick={()=>{sessionStorage.removeItem("admin_code");setOwnerCode("");}} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:12,fontFamily:"'Syne',sans-serif"}}>← Выйти</button></div>
      </div>
      <div style={{marginLeft:220,padding:28,minHeight:"100vh"}}>
        {tab==="dashboard" &&<TabDashboard  apiFetch={apiFetch}/>}
        {tab==="promo"     &&<TabPromo      apiFetch={apiFetch} showToast={showToast}/>}
        {tab==="orders"    &&<TabOrders     apiFetch={apiFetch}/>}
        {tab==="sessions"  &&<TabSessions   apiFetch={apiFetch}/>}
        {tab==="bugs"      &&<TabBugs       apiFetch={apiFetch}/>}
        {tab==="analytics" &&<TabAnalytics  apiFetch={apiFetch}/>}
        {tab==="settings"  &&<TabSettings   apiFetch={apiFetch} showToast={showToast}/>}
        {tab==="news"      &&<TabNews       apiFetch={apiFetch} showToast={showToast}/>}
        {tab==="banner"    &&<TabBanner     apiFetch={apiFetch} showToast={showToast}/>}
      </div>
      <Toast msg={toast.msg} type={toast.type} onHide={()=>setToast({msg:""})} />
    </div>
  );
}

// DASHBOARD
function TabDashboard({ apiFetch }) {
  const [stats,setStats]=useState(null), [analytics,setAnalytics]=useState(null);
  const COLORS=[C.pink,C.cyan,C.yellow,C.green,C.purple,C.orange];
  useEffect(()=>{Promise.all([apiFetch("/api/admin/stats"),apiFetch("/api/admin/analytics")]).then(([s,a])=>{setStats(s);setAnalytics(a);}).catch(()=>{});},[apiFetch]);
  if(!stats||!analytics)return <div style={{color:C.muted,padding:40,textAlign:"center"}}>Загрузка...</div>;
  return (
    <div>
      <PageHeader title="Дашборд" sub="// общая сводка"/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(165px,1fr))",gap:14,marginBottom:24}}>
        <StatCard label="Выручка"      value={`${Math.round(stats.revenue||0).toLocaleString("ru")}₽`} sub={`${stats.orders_paid} оплачено`}   color={C.pink}/>
        <StatCard label="Заказов"      value={stats.orders_total}  sub="в базе"                          color={C.cyan}/>
        <StatCard label="Промокоды"    value={stats.codes_active}  sub={`из ${stats.codes_total}`}        color={C.green}/>
        <StatCard label="Игр. сессий"  value={analytics.summary?.total_sessions||0} sub={`ср. ${analytics.summary?.avg_players||0} игр.`} color={C.yellow}/>
        <StatCard label="PRO сессии"   value={analytics.summary?.pro_sessions||0}   sub={`из ${analytics.summary?.total_sessions||0}`}    color={C.purple}/>
        <StatCard label="Ср. длит."    value={`${analytics.summary?.avg_duration_min||0}м`} sub="на сессию" color={C.orange}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <Card title="Топ игр"><div style={{padding:"16px 20px"}}>{(analytics.top_games||[]).map((g,i)=><BarRow key={g.game} label={g.game} value={g.count} max={analytics.top_games[0]?.count||1} color={COLORS[i%COLORS.length]}/>)}</div></Card>
        <Card title="Сессии по дням (30д)">
          <div style={{padding:"16px 20px",display:"flex",alignItems:"flex-end",gap:3,height:100}}>
            {(analytics.daily_last_30||[]).map(([date,cnt])=>{const maxD=Math.max(...(analytics.daily_last_30||[]).map(d=>d[1]),1);return <div key={date} title={`${date.slice(5)}: ${cnt}`} style={{flex:1,background:"rgba(255,60,172,.3)",borderRadius:"2px 2px 0 0",height:`${Math.max(4,Math.round(cnt/maxD*100))}%`,minHeight:2,cursor:"pointer"}} onMouseOver={e=>e.target.style.background=C.pink} onMouseOut={e=>e.target.style.background="rgba(255,60,172,.3)"}/>;})}
          </div>
        </Card>
      </div>
    </div>
  );
}

// PROMO
function TabPromo({ apiFetch, showToast }) {
  const [codes,setCodes]=useState([]), [loading,setLoading]=useState(true);
  const [filter,setFilter]=useState("all"), [search,setSearch]=useState("");
  const [sort,setSort]=useState({field:"created_at",dir:"desc"});
  const [page,setPage]=useState(1), [perPage,setPerPage]=useState(25);
  const [editRooms,setEditRooms]=useState({});
  const [modal,setModal]=useState(false), [newRooms,setNewRooms]=useState("15"), [newNote,setNewNote]=useState("");

  const load=useCallback(()=>{setLoading(true);apiFetch("/api/promo/list").then(d=>setCodes(d.codes||[])).catch(()=>{}).finally(()=>setLoading(false));},[apiFetch]);
  useEffect(()=>{load();},[load]);

  const handleSort=f=>{setSort(s=>s.field===f?{field:f,dir:s.dir==="asc"?"desc":"asc"}:{field:f,dir:"desc"});setPage(1);};

  const processed=useMemo(()=>{
    let list=[...codes];
    if(filter==="active")list=list.filter(c=>(c.total_rooms-c.used_rooms)>0);
    if(filter==="used")list=list.filter(c=>c.used_rooms>=c.total_rooms&&c.total_rooms>0);
    if(search.trim()){const q=search.toLowerCase();list=list.filter(c=>c.code?.toLowerCase().includes(q)||c.email?.toLowerCase().includes(q)||c.note?.toLowerCase().includes(q));}
    list.sort((a,b)=>{let va=a[sort.field]??"",vb=b[sort.field]??"";if(typeof va==="string"){va=va.toLowerCase();vb=vb.toLowerCase();}return sort.dir==="asc"?(va>vb?1:-1):(va<vb?1:-1);});
    return list;
  },[codes,filter,search,sort]);

  const paged=processed.slice((page-1)*perPage,page*perPage);

  async function saveRooms(code){const val=parseInt(editRooms[code]??codes.find(c=>c.code===code)?.total_rooms)||0;try{await apiFetch(`/api/promo/${code}/rooms`,{method:"PATCH",body:JSON.stringify({total_rooms:val})});showToast(`${code} → ${val} сессий`);load();}catch(e){showToast("Ошибка: "+e.message,"err");}}
  async function reset(code){if(!window.confirm(`Сбросить счётчик ${code}?`))return;try{await apiFetch(`/api/promo/${code}/reset`,{method:"PATCH",body:"{}"});showToast(`Сброшен: ${code}`);load();}catch{showToast("Ошибка","err");}}
  async function del(code){if(!window.confirm(`Удалить ${code}?`))return;try{await apiFetch(`/api/promo/${code}`,{method:"DELETE"});showToast(`Удалён: ${code}`);load();}catch{showToast("Ошибка","err");}}
  async function create(){try{const r=await apiFetch("/api/promo/generate",{method:"POST",body:JSON.stringify({total_rooms:parseInt(newRooms)||15,note:newNote})});showToast(`Создан: ${r.code}`);setModal(false);setNewNote("");setNewRooms("15");load();}catch{showToast("Ошибка","err");}}

  return (
    <div>
      <PageHeader title="Промокоды" sub={`// ${codes.length} кодов`} actions={<><Btn onClick={load}>↻</Btn><Btn onClick={()=>setModal(true)} color="pink">+ Создать</Btn></>}/>
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        {[["all","Все"],["active","Активные"],["used","Исчерпаны"]].map(([f,l])=><button key={f} onClick={()=>{setFilter(f);setPage(1);}} style={{padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${filter===f?C.border2:C.border}`,fontFamily:"'Syne',sans-serif",background:filter===f?C.surface2:"transparent",color:filter===f?C.text:C.muted}}>{l}</button>)}
        <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Поиск по коду, email, заметке…" style={{marginLeft:"auto",width:260}}/>
      </div>
      <Card count={processed.length} actions={<span style={{fontSize:11,color:C.muted,fontFamily:"monospace"}}>стр. {page}/{Math.max(1,Math.ceil(processed.length/perPage))}</span>}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr>
              <SortTH label="Код"          field="code"       sort={sort} onSort={handleSort}/>
              <SortTH label="Использовано" field="used_rooms"  sort={sort} onSort={handleSort}/>
              <SortTH label="Осталось"     field={null}        sort={sort} onSort={handleSort}/>
              <SortTH label="Примечание"   field="note"        sort={sort} onSort={handleSort}/>
              <SortTH label="Email"        field="email"       sort={sort} onSort={handleSort}/>
              <SortTH label="Создан"       field="created_at"  sort={sort} onSort={handleSort}/>
              <SortTH label="Действия"     field={null}        sort={sort} onSort={handleSort}/>
            </tr></thead>
            <tbody>
              {loading?<tr><td colSpan={7} style={{padding:32,textAlign:"center",color:C.muted}}>Загрузка...</td></tr>:
               paged.length===0?<tr><td colSpan={7} style={{padding:32,textAlign:"center",color:C.muted}}>Нет данных</td></tr>:
               paged.map(c=>{
                const left=c.total_rooms-c.used_rooms, pct=c.total_rooms>0?Math.min(100,Math.round(c.used_rooms/c.total_rooms*100)):0, cur=editRooms[c.code]??c.total_rooms;
                return <tr key={c.code} onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,.02)"} onMouseOut={e=>e.currentTarget.style.background=""}>
                  <td style={{padding:"10px 14px",fontFamily:"monospace"}}><div style={{display:"flex",alignItems:"center",gap:8}}><span style={{color:C.cyan}}>{c.code}</span><Badge text={left<=0?"Исчерпан":left<=3?"Мало":"Активен"} color={left<=0?"red":left<=3?"yellow":"green"}/></div></td>
                  <td style={{padding:"10px 14px",fontFamily:"monospace",fontSize:13}}>{c.used_rooms}/{c.total_rooms}<div style={{height:3,background:C.surface2,borderRadius:2,marginTop:4,width:60,overflow:"hidden"}}><div style={{height:"100%",width:`${pct}%`,background:pct>=100?C.red:pct>=70?C.yellow:C.green,borderRadius:2}}/></div></td>
                  <td style={{padding:"10px 14px",fontFamily:"monospace",fontSize:13,color:left>0?C.green:C.red}}>{left}</td>
                  <td style={{padding:"10px 14px",fontSize:12,color:C.muted,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.note||"—"}</td>
                  <td style={{padding:"10px 14px",fontSize:12,color:C.muted,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.email||"—"}</td>
                  <td style={{padding:"10px 14px",fontSize:12,color:C.muted,fontFamily:"monospace"}}>{fmtDate(c.created_at)}</td>
                  <td style={{padding:"10px 14px"}}><div style={{display:"flex",gap:5,alignItems:"center"}}><input type="number" value={cur} min="0" onChange={e=>setEditRooms(p=>({...p,[c.code]:e.target.value}))} style={{width:56,background:C.bg,border:`1px solid ${C.border2}`,borderRadius:6,padding:"4px 7px",color:C.text,fontFamily:"monospace",fontSize:12,outline:"none"}}/><Btn onClick={()=>saveRooms(c.code)} color="green" sm>✓</Btn><Btn onClick={()=>reset(c.code)} color="yellow" sm>↺</Btn><Btn onClick={()=>del(c.code)} color="red" sm>✕</Btn></div></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        <Pagination total={processed.length} page={page} perPage={perPage} onPage={p=>{setPage(p);window.scrollTo(0,0);}} onPerPage={n=>{setPerPage(n);setPage(1);}}/>
      </Card>
      <Modal open={modal} onClose={()=>setModal(false)} title="Создать промокод">
        <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Кол-во сессий</div>
        <input type="number" value={newRooms} onChange={e=>setNewRooms(e.target.value)} style={{width:"100%",background:C.bg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"10px 14px",color:C.text,fontFamily:"monospace",fontSize:14,outline:"none",marginBottom:14}}/>
        <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Примечание</div>
        <input value={newNote} onChange={e=>setNewNote(e.target.value)} placeholder="Для кого / зачем" style={{width:"100%",background:C.bg,border:`1px solid ${C.border2}`,borderRadius:8,padding:"10px 14px",color:C.text,fontFamily:"'Syne',sans-serif",fontSize:13,outline:"none",marginBottom:20}}/>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}><Btn onClick={()=>setModal(false)}>Отмена</Btn><Btn onClick={create} color="pink">Создать</Btn></div>
      </Modal>
    </div>
  );
}

// ORDERS
function TabOrders({ apiFetch }) {
  const [orders,setOrders]=useState([]), [loading,setLoading]=useState(true);
  const [search,setSearch]=useState(""), [statusFilter,setStatusFilter]=useState("");
  const [sort,setSort]=useState({field:"created_at",dir:"desc"});
  const [page,setPage]=useState(1), [perPage,setPerPage]=useState(25);

  const load=useCallback((status="")=>{setLoading(true);apiFetch(`/api/admin/orders?limit=500${status?`&status=${status}`:""}`).then(d=>setOrders(d.orders||[])).catch(()=>{}).finally(()=>setLoading(false));},[apiFetch]);
  useEffect(()=>{load();},[load]);

  const handleSort=f=>{setSort(s=>s.field===f?{field:f,dir:s.dir==="asc"?"desc":"asc"}:{field:f,dir:"desc"});setPage(1);};
  function setFilter(s){setStatusFilter(s);setPage(1);load(s);}

  const processed=useMemo(()=>{
    let list=[...orders];
    if(search.trim()){const q=search.toLowerCase();list=list.filter(o=>o.email?.toLowerCase().includes(q)||o.promo_code?.toLowerCase().includes(q)||o.plan_name?.toLowerCase().includes(q)||o.order_id?.toLowerCase().includes(q));}
    list.sort((a,b)=>{let va=a[sort.field]??"",vb=b[sort.field]??"";if(typeof va==="string"){va=va.toLowerCase();vb=vb.toLowerCase();}return sort.dir==="asc"?(va>vb?1:-1):(va<vb?1:-1);});
    return list;
  },[orders,search,sort]);

  const paged=processed.slice((page-1)*perPage,page*perPage);
  const paid=orders.filter(o=>o.status==="succeeded");
  const revenue=paid.reduce((s,o)=>s+parseFloat((o.amount||"0").replace(",",".")),0);

  return (
    <div>
      <PageHeader title="Заказы" sub="// платежи ЮКасса" actions={<Btn onClick={()=>load(statusFilter)}>↻ Обновить</Btn>}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:20}}>
        <StatCard label="Выручка"  value={`${Math.round(revenue).toLocaleString("ru")}₽`} color={C.green}/>
        <StatCard label="Оплачено" value={paid.length}                                     color={C.pink}/>
        <StatCard label="Всего"    value={orders.length}                                   color={C.cyan}/>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        {[["","Все"],["succeeded","Оплачено"],["pending","Ожидают"],["canceled","Отменены"]].map(([s,l])=><button key={s} onClick={()=>setFilter(s)} style={{padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${statusFilter===s?C.border2:C.border}`,fontFamily:"'Syne',sans-serif",background:statusFilter===s?C.surface2:"transparent",color:statusFilter===s?C.text:C.muted}}>{l}</button>)}
        <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Поиск по email, промокоду, тарифу…" style={{marginLeft:"auto",width:280}}/>
      </div>
      <Card count={processed.length}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr>
              <SortTH label="ID"       field="order_id"   sort={sort} onSort={handleSort}/>
              <SortTH label="Тариф"    field="plan_name"  sort={sort} onSort={handleSort}/>
              <SortTH label="Сумма"    field="amount"     sort={sort} onSort={handleSort}/>
              <SortTH label="Email"    field="email"      sort={sort} onSort={handleSort}/>
              <SortTH label="Промокод" field="promo_code" sort={sort} onSort={handleSort}/>
              <SortTH label="Статус"   field="status"     sort={sort} onSort={handleSort}/>
              <SortTH label="Дата"     field="created_at" sort={sort} onSort={handleSort}/>
            </tr></thead>
            <tbody>
              {loading?<tr><td colSpan={7} style={{padding:32,textAlign:"center",color:C.muted}}>Загрузка...</td></tr>:
               paged.length===0?<tr><td colSpan={7} style={{padding:32,textAlign:"center",color:C.muted}}>Нет заказов</td></tr>:
               paged.map((o,i)=><tr key={i} onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,.02)"} onMouseOut={e=>e.currentTarget.style.background=""}>
                <td style={{padding:"10px 14px",fontSize:12,color:C.muted,fontFamily:"monospace"}}>{(o.order_id||"").slice(0,8)}…</td>
                <td style={{padding:"10px 14px",fontSize:13}}>{o.plan_name||o.plan||"—"}</td>
                <td style={{padding:"10px 14px",fontSize:13,color:C.green,fontFamily:"monospace"}}>{o.amount||"—"}₽</td>
                <td style={{padding:"10px 14px",fontSize:12,color:C.muted,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.email||"—"}</td>
                <td style={{padding:"10px 14px",fontSize:13,color:C.cyan,fontFamily:"monospace"}}>{o.promo_code||"—"}</td>
                <td style={{padding:"10px 14px"}}><Badge text={o.status==="succeeded"?"Оплачено":o.status==="pending"?"Ожидает":o.status==="canceled"?"Отменён":o.status||"—"} color={o.status==="succeeded"?"green":o.status==="pending"?"yellow":o.status==="canceled"?"red":"muted"}/></td>
                <td style={{padding:"10px 14px",fontSize:12,color:C.muted,fontFamily:"monospace"}}>{fmtDate(o.paid_at||o.created_at)}</td>
               </tr>)}
            </tbody>
          </table>
        </div>
        <Pagination total={processed.length} page={page} perPage={perPage} onPage={p=>{setPage(p);window.scrollTo(0,0);}} onPerPage={n=>{setPerPage(n);setPage(1);}}/>
      </Card>
    </div>
  );
}

// SESSIONS
function TabSessions({ apiFetch }) {
  const [sessions,setSessions]=useState([]), [loading,setLoading]=useState(true);
  const [search,setSearch]=useState(""), [gameFilter,setGameFilter]=useState(""), [proFilter,setProFilter]=useState("");
  const [sort,setSort]=useState({field:"finished_at",dir:"desc"});
  const [page,setPage]=useState(1), [perPage,setPerPage]=useState(25);

  const load=useCallback(()=>{setLoading(true);apiFetch("/api/admin/sessions?limit=500").then(d=>setSessions(d.sessions||[])).catch(()=>{}).finally(()=>setLoading(false));},[apiFetch]);
  useEffect(()=>{load();},[load]);

  const handleSort=f=>{setSort(s=>s.field===f?{field:f,dir:s.dir==="asc"?"desc":"asc"}:{field:f,dir:"desc"});setPage(1);};
  const gameTypes=useMemo(()=>[...new Set(sessions.map(s=>s.game_type).filter(Boolean))].sort(),[sessions]);

  const processed=useMemo(()=>{
    let list=[...sessions];
    if(gameFilter)list=list.filter(s=>s.game_type===gameFilter);
    if(proFilter==="pro")list=list.filter(s=>s.is_pro);
    if(proFilter==="free")list=list.filter(s=>!s.is_pro);
    if(search.trim()){const q=search.toLowerCase();list=list.filter(s=>s.room_id?.toLowerCase().includes(q)||s.game_type?.toLowerCase().includes(q));}
    list.sort((a,b)=>{let va=a[sort.field]??"",vb=b[sort.field]??"";if(typeof va==="string"){va=va.toLowerCase();vb=vb.toLowerCase();}return sort.dir==="asc"?(va>vb?1:-1):(va<vb?1:-1);});
    return list;
  },[sessions,gameFilter,proFilter,search,sort]);

  const paged=processed.slice((page-1)*perPage,page*perPage);

  return (
    <div>
      <PageHeader title="Игровые сессии" sub={`// ${sessions.length} сессий`} actions={<Btn onClick={load}>↻ Обновить</Btn>}/>
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <Select value={gameFilter} onChange={e=>{setGameFilter(e.target.value);setPage(1);}} options={[["","Все игры"],...gameTypes.map(g=>[g,g])]}/>
        <Select value={proFilter} onChange={e=>{setProFilter(e.target.value);setPage(1);}} options={[["","PRO + Free"],["pro","Только PRO"],["free","Только Free"]]}/>
        <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Поиск по комнате…" style={{marginLeft:"auto",width:220}}/>
      </div>
      <Card count={processed.length}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr>
              <SortTH label="Комната"   field="room_id"       sort={sort} onSort={handleSort}/>
              <SortTH label="Игра"      field="game_type"     sort={sort} onSort={handleSort}/>
              <SortTH label="Игроки"    field="player_count"  sort={sort} onSort={handleSort}/>
              <SortTH label="Раунды"    field="rounds_played" sort={sort} onSort={handleSort}/>
              <SortTH label="Длит."     field="duration_sec"  sort={sort} onSort={handleSort}/>
              <SortTH label="PRO"       field="is_pro"        sort={sort} onSort={handleSort}/>
              <SortTH label="Завершена" field="finished_at"   sort={sort} onSort={handleSort}/>
            </tr></thead>
            <tbody>
              {loading?<tr><td colSpan={7} style={{padding:32,textAlign:"center",color:C.muted}}>Загрузка...</td></tr>:
               paged.length===0?<tr><td colSpan={7} style={{padding:32,textAlign:"center",color:C.muted}}>Нет сессий</td></tr>:
               paged.map((s,i)=><tr key={i} onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,.02)"} onMouseOut={e=>e.currentTarget.style.background=""}>
                <td style={{padding:"10px 14px",fontSize:13,color:C.cyan,fontFamily:"monospace"}}>{s.room_id||"—"}</td>
                <td style={{padding:"10px 14px"}}><Badge text={s.game_type||"—"} color="purple"/></td>
                <td style={{padding:"10px 14px",fontSize:13,fontFamily:"monospace"}}>{s.player_count||0}</td>
                <td style={{padding:"10px 14px",fontSize:13,color:C.muted,fontFamily:"monospace"}}>{s.rounds_played||s.max_rounds||"—"}</td>
                <td style={{padding:"10px 14px",fontSize:12,color:C.muted,fontFamily:"monospace"}}>{fmtDur(s.duration_sec)}</td>
                <td style={{padding:"10px 14px"}}><Badge text={s.is_pro?"PRO":"Free"} color={s.is_pro?"cyan":"muted"}/></td>
                <td style={{padding:"10px 14px",fontSize:12,color:C.muted,fontFamily:"monospace"}}>{fmtDate(s.finished_at)}</td>
               </tr>)}
            </tbody>
          </table>
        </div>
        <Pagination total={processed.length} page={page} perPage={perPage} onPage={p=>{setPage(p);window.scrollTo(0,0);}} onPerPage={n=>{setPerPage(n);setPage(1);}}/>
      </Card>
    </div>
  );
}

// BUGS
function TabBugs({ apiFetch }) {
  const [reports,setReports]=useState([]), [loading,setLoading]=useState(true);
  const [search,setSearch]=useState(""), [page,setPage]=useState(1), [perPage,setPerPage]=useState(10);

  const load=useCallback(()=>{setLoading(true);apiFetch("/api/admin/bug-reports?limit=200").then(d=>setReports(d.reports||[])).catch(()=>{}).finally(()=>setLoading(false));},[apiFetch]);
  useEffect(()=>{load();},[load]);

  const processed=useMemo(()=>{if(!search.trim())return reports;const q=search.toLowerCase();return reports.filter(r=>r.description?.toLowerCase().includes(q)||r.email?.toLowerCase().includes(q)||r.page?.toLowerCase().includes(q));},[reports,search]);
  const paged=processed.slice((page-1)*perPage,page*perPage);

  return (
    <div>
      <PageHeader title="Баг-репорты" sub={`// ${reports.length} репортов`} actions={<Btn onClick={load}>↻ Обновить</Btn>}/>
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
        <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Поиск по описанию, email, странице…" style={{width:320}}/>
        <span style={{fontSize:12,color:C.muted,fontFamily:"monospace",marginLeft:"auto"}}>Найдено: {processed.length}</span>
      </div>
      {loading?<div style={{color:C.muted,padding:40,textAlign:"center"}}>Загрузка...</div>:
       paged.length===0?<div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:40,textAlign:"center",color:C.muted}}>{search?"Ничего не найдено":"Нет репортов 🎉"}</div>:(
        <>
          {paged.map(r=>(
            <div key={r.report_id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18,marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10,marginBottom:10}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}><Badge text={`#${(r.report_id||"").slice(0,6)}`} color="red"/><span style={{fontSize:12,color:C.muted,fontFamily:"monospace"}}>{fmtDate(r.created_at)}</span></div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                {r.email&&<span style={{fontSize:12,color:C.muted}}>{r.email}</span>}
                {r.phone&&<span style={{fontSize:12,color:"#00f5ff"}}>📱 {r.phone}</span>}
                {r.page&&<Badge text={r.page} color="muted"/>}
              </div>
              </div>
              <div style={{background:C.bg,borderRadius:8,padding:12,fontSize:13,color:C.text,lineHeight:1.6,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{r.description||"—"}</div>
            </div>
          ))}
          <Pagination total={processed.length} page={page} perPage={perPage} onPage={p=>{setPage(p);window.scrollTo(0,0);}} onPerPage={n=>{setPerPage(n);setPage(1);}}/>
        </>
      )}
    </div>
  );
}

// ANALYTICS
function TabAnalytics({ apiFetch }) {
  const [data,setData]=useState(null), [loading,setLoading]=useState(true);
  const COLORS=[C.pink,C.cyan,C.yellow,C.green,C.purple,C.orange];
  const load=useCallback(()=>{setLoading(true);apiFetch("/api/admin/analytics").then(setData).catch(()=>{}).finally(()=>setLoading(false));},[apiFetch]);
  useEffect(()=>{load();},[load]);
  if(loading)return <div style={{color:C.muted,padding:40,textAlign:"center"}}>Загрузка...</div>;
  if(!data)return <div style={{color:C.red,padding:40,textAlign:"center"}}>Ошибка загрузки</div>;
  const s=data.summary||{};
  return (
    <div>
      <PageHeader title="Аналитика" sub="// статистика игр" actions={<Btn onClick={load}>↻ Обновить</Btn>}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(155px,1fr))",gap:14,marginBottom:24}}>
        <StatCard label="Всего сессий"    value={s.total_sessions||0}  color={C.pink}/>
        <StatCard label="Всего игроков"   value={s.total_players||0}   color={C.cyan}/>
        <StatCard label="Среднее игроков" value={s.avg_players||0}     color={C.yellow}/>
        <StatCard label="PRO сессии"      value={s.pro_sessions||0}    sub={`${s.free_sessions||0} бесплатных`} color={C.purple}/>
        <StatCard label="Ср. длит."       value={`${s.avg_duration_min||0}м`} color={C.orange}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <Card title="Топ игр по сессиям"><div style={{padding:"16px 20px"}}>{(data.top_games||[]).map((g,i)=><BarRow key={g.game} label={g.game} value={g.count} max={data.top_games[0]?.count||1} color={COLORS[i%COLORS.length]}/>)}</div></Card>
        <Card title="Ср. игроков по игре"><div style={{padding:"16px 20px"}}>{Object.entries(data.avg_players_by_game||{}).sort((a,b)=>b[1]-a[1]).slice(0,10).map(([g,avg],i)=><BarRow key={g} label={g} value={avg} max={Math.max(...Object.values(data.avg_players_by_game||{}),1)} color={COLORS[i%COLORS.length]}/>)}</div></Card>
      </div>
      {data.player_dist&&(
        <Card title="Распределение по кол-ву игроков">
          <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr><th style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:1,borderBottom:`1px solid ${C.border}`}}>Игроков</th><th style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:1,borderBottom:`1px solid ${C.border}`}}>Сессий</th><th style={{padding:"9px 14px",textAlign:"left",fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:1,borderBottom:`1px solid ${C.border}`}}>Доля</th></tr></thead>
            <tbody>{Object.entries(data.player_dist).sort((a,b)=>Number(a[0])-Number(b[0])).map(([cnt,n])=>{const total=Object.values(data.player_dist).reduce((a,b)=>a+b,0);return <tr key={cnt} onMouseOver={e=>e.currentTarget.style.background="rgba(255,255,255,.02)"} onMouseOut={e=>e.currentTarget.style.background=""}><td style={{padding:"10px 14px",fontFamily:"monospace",fontSize:13}}>{cnt} чел.</td><td style={{padding:"10px 14px",fontFamily:"monospace",fontSize:13,color:C.cyan}}>{n}</td><td style={{padding:"10px 14px"}}><div style={{display:"flex",alignItems:"center",gap:8}}><div style={{width:80,height:4,background:C.surface2,borderRadius:2,overflow:"hidden"}}><div style={{height:"100%",width:`${Math.round(n/total*100)}%`,background:C.pink,borderRadius:2}}/></div><span style={{fontSize:12,color:C.muted,fontFamily:"monospace"}}>{Math.round(n/total*100)}%</span></div></td></tr>;})}</tbody>
          </table></div>
        </Card>
      )}
    </div>
  );
}
// ── Tab: Настройки ────────────────────────────────────────────────────────────
const GAME_LABELS = {
  quiz:"Викторина", quiz_cinema:"Кино-викторина", quiz_18:"Викторина 18+", quiz_movies:"Угадай фильм",
  drawing:"Рисовалка", voting:"Голосование", truthdare:"Правда или действие",
  crocodile:"Крокодил", quiplash:"Закончи фразу", fibbage:"Фиббедж",
  fact_or_fake:"Правда или фейк", emoji_movie:"Фильм по эмодзи",
  dictator:"Диктатор", two_truths:"Два правды одна ложь",
  truth:"Правда (контент)", dare:"Действие (контент)",
  jeopardy:"Своя игра",
  reaction:"Реакция",
  screenplay:"Безумный сценарист",
  spyfall:"Шпион",
};

function Toggle({ value, onChange, disabled }) {
  return (
    <div onClick={()=>!disabled&&onChange(!value)} style={{
      width:36, height:20, borderRadius:10, cursor:disabled?"not-allowed":"pointer",
      background:value?"#3dffa0":"#ffffff18", border:`1px solid ${value?"#3dffa060":"#ffffff20"}`,
      position:"relative", transition:"background .2s", opacity:disabled?.35:1, flexShrink:0,
    }}>
      <div style={{
        position:"absolute", top:2, left:value?18:2, width:14, height:14,
        borderRadius:"50%", background:value?"#000":"#6060a0", transition:"left .2s",
      }}/>
    </div>
  );
}

function TabSettings({ apiFetch, showToast }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch("/api/admin/settings").then(d => setSettings(d.settings)).catch(() => {});
  }, [apiFetch]);

  const toggle = (game, field) => {
    setSettings(prev => ({
      ...prev,
      [game]: { ...prev[game], [field]: !prev[game][field] }
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/admin/settings", {
        method: "POST",
        body: JSON.stringify({ settings }),
      });
      showToast("Настройки сохранены");
    } catch { showToast("Ошибка сохранения", "err"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div style={{color:C.muted,padding:24,fontFamily:"'Syne',sans-serif",fontSize:13}}>Загрузка...</div>;

  const games = Object.entries(settings);

  return (
    <div style={{padding:"0 4px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div>
          <div style={{color:C.text,fontFamily:"'Syne',sans-serif",fontSize:16,fontWeight:700,marginBottom:4}}>Настройки игр</div>
          <div style={{color:C.muted,fontFamily:"'Syne',sans-serif",fontSize:12}}>Управление AI-генерацией и TTS озвучкой по каждой игре. Сбрасывается при перезапуске сервера.</div>
        </div>
        <Btn color="green" onClick={save} disabled={saving}>{saving?"Сохранение...":"Сохранить"}</Btn>
      </div>

      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
        {/* Шапка */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 120px 120px",padding:"10px 16px",borderBottom:`1px solid ${C.border}`,background:C.surface2}}>
          <div style={{color:C.muted,fontFamily:"'Syne',sans-serif",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:1}}>Игра</div>
          <div style={{color:C.muted,fontFamily:"'Syne',sans-serif",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:1,textAlign:"center"}}>🤖 AI контент</div>
          <div style={{color:C.muted,fontFamily:"'Syne',sans-serif",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:1,textAlign:"center"}}>🔊 Озвучка</div>
        </div>

        {games.map(([game, cfg], i) => (
          <div key={game} style={{
            display:"grid", gridTemplateColumns:"1fr 120px 120px",
            padding:"12px 16px", alignItems:"center",
            borderBottom: i < games.length-1 ? `1px solid ${C.border}` : "none",
            background: i%2===0 ? "transparent" : "rgba(255,255,255,.02)",
          }}>
            <div style={{color:C.text,fontFamily:"'Syne',sans-serif",fontSize:13}}>
              {GAME_LABELS[game]||game}
              <span style={{color:C.muted,fontSize:11,marginLeft:8}}>{game}</span>
            </div>
            <div style={{display:"flex",justifyContent:"center"}}>
              {cfg.ai_supported
                ? <Toggle value={cfg.ai} onChange={()=>toggle(game,"ai")} />
                : <span style={{color:C.muted,fontSize:11,fontFamily:"'Syne',sans-serif"}}>—</span>
              }
            </div>
            <div style={{display:"flex",justifyContent:"center"}}>
              {cfg.tts_supported
                ? <Toggle value={cfg.tts} onChange={()=>toggle(game,"tts")} />
                : <span style={{color:C.muted,fontSize:11,fontFamily:"'Syne',sans-serif"}}>—</span>
              }
            </div>
          </div>
        ))}
      </div>

      <div style={{marginTop:16,display:"flex",justifyContent:"flex-end"}}>
        <Btn color="green" onClick={save} disabled={saving}>{saving?"Сохранение...":"Сохранить"}</Btn>
      </div>
    </div>
  );
}

// ── Tab: Новости ──────────────────────────────────────────────────────────────
function TabNews({ apiFetch, showToast }) {
  const [news, setNews]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [emoji, setEmoji]         = useState("📢");
  const [coverEmoji, setCoverEmoji] = useState("🎉");
  const [title, setTitle]         = useState("");
  const [text, setText]           = useState("");
  const [category, setCategory]   = useState("news");
  const [readTime, setReadTime]   = useState(3);
  const [tags, setTags]           = useState("");
  const [saving, setSaving]       = useState(false);
  const [editId, setEditId]       = useState(null); // id редактируемой новости

  const CATS = [
    { id: "news",   label: "Новости"    },
    { id: "game",   label: "Новая игра" },
    { id: "tips",   label: "Советы"     },
    { id: "party",  label: "Вечеринка"  },
    { id: "update", label: "Обновление" },
    { id: "blog",   label: "Блог"       },
  ];

  const TEXTAREABASE = {
    width: "100%", background: C.surface2, border: `1px solid ${C.border2}`,
    borderRadius: 8, padding: "7px 12px", color: C.text,
    fontFamily: "'Syne',sans-serif", fontSize: 13, outline: "none",
    resize: "vertical", boxSizing: "border-box",
  };

  const load = () => {
    setLoading(true);
    apiFetch("/api/news?limit=100").then(d => setNews(d.news || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearForm = () => {
    setTitle(""); setText(""); setEmoji("📢"); setCoverEmoji("🎉");
    setCategory("news"); setReadTime(3); setTags(""); setEditId(null);
  };

  const startEdit = (n) => {
    setEditId(n.id);
    setTitle(n.title); setText(n.text || ""); setEmoji(n.emoji || "📢");
    setCoverEmoji(n.cover_emoji || ""); setCategory(n.category || "news");
    setReadTime(n.read_time || 3); setTags((n.tags || []).join(", "));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const body = {
      emoji, cover_emoji: coverEmoji, title, text,
      category, read_time: Number(readTime),
      tags: tags.split(",").map(t => t.trim()).filter(Boolean),
    };
    try {
      if (editId) {
        await apiFetch(`/api/admin/news/${editId}`, { method: "PATCH", body: JSON.stringify(body) });
        showToast("Новость обновлена");
      } else {
        await apiFetch("/api/admin/news", { method: "POST", body: JSON.stringify(body) });
        showToast("Новость добавлена");
      }
      clearForm();
      load();
    } catch { showToast("Ошибка", "err"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      await apiFetch(`/api/admin/news/${id}`, { method: "DELETE" });
      showToast("Удалено");
      if (editId === id) clearForm();
      load();
    } catch { showToast("Ошибка", "err"); }
  };

  const LINK = `${window.location.origin}/news`;

  return (
    <div style={{ padding: "0 4px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700 }}>
          {editId ? "✏️ Редактировать" : "📰 Новости блога"}
        </div>
        <a href={LINK} target="_blank" rel="noreferrer" style={{
          fontFamily: "'Syne',sans-serif", fontSize: 11, color: "#9b5de5",
          textDecoration: "none", background: "#1a0035", borderRadius: 6,
          padding: "3px 10px", border: "1px solid #9b5de544",
        }}>
          Открыть блог ↗
        </a>
      </div>

      {/* Форма */}
      <div style={{ background: C.surface, border: `1px solid ${editId ? "#9b5de5" : C.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
        <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
          {editId ? "Редактировать публикацию" : "Новая публикация"}
        </div>

        {/* Строка 1: эмодзи + обложка + заголовок */}
        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Иконка</div>
            <Input value={emoji} onChange={e => setEmoji(e.target.value)} placeholder="📢" style={{ width: 60 }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Обложка</div>
            <Input value={coverEmoji} onChange={e => setCoverEmoji(e.target.value)} placeholder="🎉" style={{ width: 70 }} />
          </div>
          <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Заголовок</div>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Заголовок публикации" />
          </div>
        </div>

        {/* Строка 2: категория + время чтения */}
        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Категория</div>
            <select
              value={category} onChange={e => setCategory(e.target.value)}
              style={{ background: C.surface2, border: `1px solid ${C.border2}`, borderRadius: 8, padding: "7px 10px", color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 12, outline: "none" }}
            >
              {CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Мин. чтения</div>
            <Input type="number" value={readTime} onChange={e => setReadTime(e.target.value)} style={{ width: 70 }} min={1} max={60} />
          </div>
          <div style={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Теги (через запятую)</div>
            <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="игра, вечеринка, советы" />
          </div>
        </div>

        {/* Текст */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10, marginBottom: 4 }}>
            Текст статьи — поддерживает ## Заголовки, - списки, двойные переносы = новый абзац
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={"Текст статьи...\n\n## Подзаголовок\n\n- Пункт списка\n- Ещё пункт"}
            rows={8}
            style={{ ...TEXTAREABASE, marginBottom: 0 }}
          />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <Btn color="pink" onClick={save} disabled={saving || !title.trim()}>
            {saving ? "Сохранение..." : editId ? "Сохранить" : "Опубликовать"}
          </Btn>
          {editId && <Btn onClick={clearForm}>Отмена</Btn>}
        </div>
      </div>

      {/* Список */}
      {loading ? (
        <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 13 }}>Загрузка...</div>
      ) : news.length === 0 ? (
        <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 13 }}>Новостей нет</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {news.map(n => (
            <div key={n.id} style={{
              background: editId === n.id ? "#1a0035" : C.surface,
              border: `1px solid ${editId === n.id ? "#9b5de5" : C.border}`,
              borderRadius: 10, padding: "10px 14px",
              display: "flex", alignItems: "flex-start", gap: 10,
            }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>{n.cover_emoji || n.emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 13, fontWeight: 700 }}>{n.emoji} {n.title}</div>
                  {n.category && (
                    <span style={{ background: "#ffffff08", borderRadius: 4, padding: "1px 6px", fontSize: 10, color: C.muted, fontFamily: "'Syne',sans-serif" }}>
                      {n.category}
                    </span>
                  )}
                </div>
                {n.text && (
                  <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 400 }}>
                    {n.text}
                  </div>
                )}
                <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10, marginTop: 3 }}>
                  {new Date(n.created_at).toLocaleString("ru")}
                  {n.read_time && ` · ${n.read_time} мин`}
                  {n.tags?.length > 0 && ` · #${n.tags.join(" #")}`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Btn sm onClick={() => startEdit(n)}>✏️</Btn>
                <Btn sm color="red" onClick={() => del(n.id)}>Удалить</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TabBanner({ apiFetch, showToast }) {
  const [title, setTitle]       = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [emoji, setEmoji]       = useState("🎁");
  const [tag, setTag]           = useState("");
  const [ctaText, setCtaText]   = useState("");
  const [ctaUrl, setCtaUrl]     = useState("/pro");
  const [style, setStyle]       = useState("purple");
  const [saving, setSaving]     = useState(false);
  const [current, setCurrent]   = useState(null);
  const [loading, setLoading]   = useState(true);

  const STYLES_LIST = [
    { id: "purple", label: "💜 Фиолетовый" },
    { id: "fire",   label: "🔥 Огонь"      },
    { id: "neon",   label: "💎 Неон"        },
    { id: "gold",   label: "✨ Золото"      },
    { id: "pink",   label: "💗 Розовый"     },
  ];

  const load = () => {
    setLoading(true);
    apiFetch("/api/banner").then(d => {
      setCurrent(d.banner || null);
      if (d.banner) {
        setTitle(d.banner.title || "");
        setSubtitle(d.banner.subtitle || "");
        setEmoji(d.banner.emoji || "🎁");
        setTag(d.banner.tag || "");
        setCtaText(d.banner.cta_text || "");
        setCtaUrl(d.banner.cta_url || "/pro");
        setStyle(d.banner.style || "purple");
      }
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await apiFetch("/api/admin/banner", {
        method: "POST",
        body: JSON.stringify({ title, subtitle, emoji, tag, cta_text: ctaText, cta_url: ctaUrl, style, active: true }),
      });
      showToast("Баннер опубликован!");
      load();
    } catch { showToast("Ошибка", "err"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try {
      await apiFetch("/api/admin/banner", { method: "DELETE" });
      showToast("Баннер скрыт");
      setCurrent(null);
      setTitle(""); setSubtitle(""); setEmoji("🎁"); setTag(""); setCtaText(""); setCtaUrl("/pro");
    } catch { showToast("Ошибка", "err"); }
  };

  const COLORS = { purple: "#9b5de5", fire: "#ff6b00", neon: "#00f5ff", gold: "#ffe600", pink: "#ff3cac" };

  return (
    <div style={{ padding: "0 4px" }}>
      <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 6 }}>🎁 Промо-акции</div>
      <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 11, marginBottom: 16 }}>
        Один активный баннер показывается на главной странице и на лейдинге. Меняй под любую акцию за 30 секунд.
      </div>

      {/* Текущий баннер */}
      {!loading && current && (
        <div style={{ background: C.surface, border: `2px solid ${COLORS[current.style] || C.border}55`, borderRadius: 10, padding: "12px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 24 }}>{current.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 13, fontWeight: 700 }}>{current.title}</div>
            {current.subtitle && <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 11, marginTop: 2 }}>{current.subtitle}</div>}
            <div style={{ color: COLORS[current.style] || C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10, marginTop: 4 }}>
              ● Активен · стиль: {current.style}
            </div>
          </div>
          <Btn sm color="red" onClick={remove}>Скрыть</Btn>
        </div>
      )}
      {!loading && !current && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", marginBottom: 16, color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 12 }}>
          Нет активного баннера
        </div>
      )}

      {/* Редактор */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
        <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
          Новый баннер
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Эмодзи</div>
            <Input value={emoji} onChange={e => setEmoji(e.target.value)} style={{ width: 60 }} />
          </div>
          <div style={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Заголовок *</div>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="🎁 Розыгрыш неонового набора!" />
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10, marginBottom: 4 }}>Подзаголовок</div>
          <Input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Купи PRO в октябре — участвуешь автоматически" />
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 120, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Тег (маленький лейбл)</div>
            <Input value={tag} onChange={e => setTag(e.target.value)} placeholder="РОЗЫГРЫШ" />
          </div>
          <div style={{ flex: 1, minWidth: 120, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Текст кнопки</div>
            <Input value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Участвовать" />
          </div>
          <div style={{ flex: 1, minWidth: 140, display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10 }}>Ссылка кнопки</div>
            <Input value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} placeholder="/pro" />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ color: C.muted, fontFamily: "'Syne',sans-serif", fontSize: 10, marginBottom: 6 }}>Стиль</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STYLES_LIST.map(s => (
              <button key={s.id} onClick={() => setStyle(s.id)} style={{
                background: style === s.id ? `${COLORS[s.id]}22` : C.surface2,
                border: `1.5px solid ${style === s.id ? COLORS[s.id] : C.border2}`,
                borderRadius: 6, padding: "5px 10px",
                fontFamily: "'Syne',sans-serif", fontSize: 11,
                color: style === s.id ? COLORS[s.id] : C.muted,
                cursor: "pointer", transition: "all .15s",
              }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <Btn color="pink" onClick={save} disabled={saving || !title.trim()}>
          {saving ? "Публикация..." : "Опубликовать баннер"}
        </Btn>
      </div>
    </div>
  );
}